<template>
  <div class="rm-page">
    <!-- 顶部统计行（风格对齐网络育人创新联盟列表页 text1/color-red） -->
    <div class="rm-summary">
      <div class="text1">
        当前共有
        <span class="color-red">{{ total }}</span>
        个分社已上传报名表
      </div>
      <a-button
        class="rm-export-btn"
        type="primary"
        icon="download"
        :loading="exporting"
        @click="handleExport"
        >导出全部报名表</a-button
      >
    </div>
       
    <!-- 表格（分页与其它列表页保持一致：20 条/页，翻页/换页大小重拉数据） -->
    <a-table
      class="rm-table"
      :columns="columns"
      :data-source="list"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
      :locale="{ emptyText: '暂无报名表，请分社账号先完成上传' }"
      :bordered="true"
      rowKey="id"
    >
      <template slot="seq" slot-scope="text, record, index">
        <span>{{ index + 1 + (pagination.current - 1) * pagination.pageSize }}</span>
      </template>
      <template slot="branchName" slot-scope="text, record">
        <span class="rm-branch">{{ branchNameOf(record) }}</span>
      </template>
      <template slot="fileTitle" slot-scope="text, record">
        <span class="rm-file-name">{{ text || fileName(record) }}</span>
      </template>
      <template slot="action" slot-scope="text, record">
        <a class="rm-preview-link" @click="openPreview(record)">
          <a-icon type="eye" /> 报名表预览
        </a>
      </template>
      <template slot="photoPreview" slot-scope="text, record">
        <a
          class="rm-preview-link"
          v-if="record.photoPath"
          @click="openPhotoPreview(record)"
        >
          <a-icon type="picture" /> 盖章图片预览
        </a>
        <span v-else class="rm-no-photo">—</span>
      </template>
    </a-table>

    <!-- 报名表 PDF 预览弹窗（完全复刻首页高职组作品弹窗结构：遮罩 + img19 框 + 红色 header） -->
    <div class="rm-preview-pop" v-if="previewVisible">
      <div class="rm-preview-box">
        <div class="rm-c-box">
          <!-- 头部：红底白字（对齐 preview.vue 的 .header-box） -->
          <div class="rm-header-box">
            <p class="rm-header-title" :title="previewRecord ? branchNameOf(previewRecord) : ''">
              报名表预览<span v-if="previewRecord">（{{ branchNameOf(previewRecord) }}）</span>
            </p>
            <div class="rm-close-box" @click="closePreview">×</div>
          </div>

          <!-- 内容区（对齐 preview.vue 的 .centent-box / .left-preview-box / .img-preview-box） -->
          <div class="rm-centent-box">
            <div class="rm-left-preview-box">
              <!-- 加载中 -->
              <div class="rm-state-box" v-if="!pdfUrl && !previewFailed">
                正在获取报名表…
              </div>
              <!-- blob 拉取中/后端转换中 -->
              <div class="rm-state-box" v-else-if="pdfUrl && !previewSrc && !previewFailed">
                <p>PDF 正在生成/加载中，约需几秒，请稍候…</p>
                <p class="rm-state-tip">
                  若长时间无响应，请<a :href="pdfUrl" target="_blank">新标签页查看</a>
                </p>
              </div>
              <!-- PDF 内嵌被拦截 -->
              <div class="rm-state-box" v-else-if="pdfUrl && previewFailed">
                <p>当前浏览器禁止在页面内嵌预览该 PDF</p>
                <a :href="pdfUrl" target="_blank" class="rm-state-link">新标签页查看</a>
              </div>
              <!-- 正常预览 -->
              <div class="rm-img-preview-box" v-else-if="previewSrc">
                <iframe
                  :src="previewSrc"
                  style="width: 100%; height: 100%; background: #fff"
                  scrolling="auto"
                  frameborder="0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 盖章图片预览弹窗（与报名表预览弹窗同款外壳：遮罩 + 红底 header + 白底内容） -->
    <div class="rm-preview-pop" v-if="photoModalVisible">
      <div class="rm-preview-box">
        <div class="rm-c-box">
          <div class="rm-header-box">
            <p class="rm-header-title">
              盖章图片预览<span v-if="photoModalRecord">（{{ branchNameOf(photoModalRecord) }}）</span>
            </p>
            <div class="rm-close-box" @click="photoModalVisible = false">×</div>
          </div>
          <div class="rm-centent-box">
            <div class="rm-left-preview-box rm-photo-wrap">
              <div class="rm-photo-bar">
                <a-button size="small" @click="photoZoomOut">
                  <a-icon type="zoom-out" />缩小
                </a-button>
                <span class="rm-photo-scale">{{ Math.round(photoZoom * 100) }}%</span>
                <a-button size="small" @click="photoZoomIn">
                  <a-icon type="zoom-in" />放大
                </a-button>
                <a-button size="small" @click="photoZoomReset">重置</a-button>
                <span class="rm-photo-tip">滚轮缩放，按住图片可拖拽平移</span>
              </div>
              <div
                class="rm-img-preview-box rm-photo-body"
                @wheel.prevent="photoWheel"
                @mousemove="photoDragMove"
                @mouseup="photoDragEnd"
                @mouseleave="photoDragEnd"
              >
                <img
                  :src="photoSrc"
                  class="rm-photo-img"
                  :style="photoImgStyle"
                  alt="盖章图片"
                  draggable="false"
                  @mousedown.prevent="photoDragStart"
                  @dblclick="photoDragReset"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { lxDrawFileList, userLxList, lxDrawTeamExportAllTeamForms } from "@/api/index";
import { baseURL } from "@/utils/request";

export default {
  name: "RegistrationManage",
  data() {
    return {
      loading: false,
      exporting: false, // 导出全部报名表 zip 中
      list: [], // 报名表记录列表（当前页）
      total: 0,
      // 分页（每页 100 条，不允许切换每页大小）
      pagination: {
        current: 1,
        pageSize: 100,
        showTotal: (t) => `共 ${t} 条`,
        showSizeChanger: false,
      },
      branchMap: {}, // branchId -> 分社名称
      columns: [
        {
          title: "序号",
          dataIndex: "_seq",
          width: 80,
          align: "center",
          scopedSlots: { customRender: "seq" },
        },
        {
          title: "分社名称",
          dataIndex: "branchId_dictText",
          width: 280,
          align: "center",
          scopedSlots: { customRender: "branchName" },
        },
        // {
        //   title: "报名表文件",
        //   dataIndex: "title",
        //   align: "center",
        //   scopedSlots: { customRender: "fileTitle" },
        // },
        {
          title: "报名表文件",
          // width: 140,
          align: "center",
          scopedSlots: { customRender: "action" },
        },
        {
          title: "盖章图片",
          align: "center",
          scopedSlots: { customRender: "photoPreview" },
        },
      ],
      // 预览弹窗状态
      previewVisible: false,
      previewRecord: null,
      pdfPath: "",
      previewSrc: "",
      previewFailed: false,
      // 盖章图片预览弹窗状态（与上传页同源弹窗）
      photoModalVisible: false,
      photoModalRecord: null,
      photoZoom: 1, // 图片预览缩放比例（1=原始大小）
      photoOffsetX: 0, // 拖拽平移偏移 X（屏幕像素）
      photoOffsetY: 0, // 拖拽平移偏移 Y（屏幕像素）
      photoDragging: false, // 是否正在拖拽
      photoDragX: 0, // 拖拽起始鼠标 X
      photoDragY: 0, // 拖拽起始鼠标 Y
    };
  },
  computed: {
    // PDF 完整预览地址（blob 拉取用）
    pdfUrl() {
      if (!this.pdfPath) return "";
      const raw = String(this.pdfPath).trim();
      if (/^https?:\/\//i.test(raw)) return raw;
      const rel = raw.replace(/^\/+/, "").replace(/^jeecg-boot\/+/, "");
      return `${baseURL}${rel}`;
    },
    // 盖章图片完整预览地址（来自当前预览记录 photoPath）
    photoSrc() {
      if (!this.photoModalRecord) return "";
      const raw = String(this.photoModalRecord.photoPath || "").trim();
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;
      const rel = raw.replace(/^\/+/, "").replace(/^jeecg-boot\/+/, "");
      return `${baseURL}${rel}`;
    },
    // 图片预览：缩放 + 平移的合并样式（拖拽中去掉过渡，跟手更顺）
    photoImgStyle() {
      return {
        transform:
          "translate(" +
          this.photoOffsetX +
          "px, " +
          this.photoOffsetY +
          "px) scale(" +
          this.photoZoom +
          ")",
        transition: this.photoDragging ? "none" : "transform 0.12s ease-out",
        cursor: this.photoDragging ? "grabbing" : "grab",
      };
    },
  },
  beforeDestroy() {
    this.revokePreview();
  },
  mounted() {
    this.loadBranchMap();
    this.loadData();
  },
  methods: {
    // 拉取当前页的报名表记录（后端分页：传 pageNo/pageSize）
    loadData() {
      this.loading = true;
      const { current, pageSize } = this.pagination;
      lxDrawFileList({ pageNo: current, pageSize })
        .then((res) => {
          if (res.code === 200 && res.result != null) {
            const result = res.result || {};
            // 标准分页结构 { records: [], total: N }
            if (Array.isArray(result.records) || typeof result.total !== "undefined") {
              this.list = (Array.isArray(result.records) ? result.records : [])
                .filter((r) => r && (r.previewPath || r.filePath));
              this.total = Number(result.total) || this.list.length;
            } else if (Array.isArray(result)) {
              // 兼容：直接返回数组（无 total 时按数组长度）
              this.list = result.filter(
                (r) => r && (r.previewPath || r.filePath)
              );
              this.total = this.list.length;
            } else {
              // 兼容：单对象
              this.list = result.previewPath || result.filePath ? [result] : [];
              this.total = this.list.length;
            }
          } else {
            this.list = [];
            this.total = 0;
          }
        })
        .catch(() => {
          this.list = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 表格分页变化：翻页 / 切换每页大小
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadData();
    },
    // 导出全部报名表（lx/lxDrawTeam/exportAllTeamForms，接口返回 JSON：含 zip 相对路径）
    handleExport() {
      if (this.exporting) return;
      this.exporting = true;
      lxDrawTeamExportAllTeamForms({})
        .then((res) => {
          // 500/未登录时，request.js 拦截器 Promise.reject(error) 缺 return，
          // 会把异常 resolve 成 undefined，这里兜住
          if (!res) {
            this.$message.error("导出失败，请确认已登录且文件已生成");
            return;
          }
          if (res.code !== 200 || !res.result) {
            this.$message.error(res.message || "导出失败，请稍后重试");
            return;
          }
          // res.result 形如 sys/common/static/template_zip/抽签报名表_20260908120000.zip
          // 拼成完整可访问 URL（兼容带/不带前导斜杠、带/不带 jeecg-boot 前缀的相对路径）
          const rel = String(res.result)
            .trim()
            .replace(/^\/+/, "")
            .replace(/^jeecg-boot\/+/, "");
          const url = baseURL + rel;
          // 下载文件名取路径中的原文件名（含时间戳），缺省回退
          const fileName =
            String(res.result).split("/").pop() || "抽签报名表.zip";
          const a = document.createElement("a");
          a.href = url;
          // 同源 download 才生效，zip 浏览器不识别会直接下载
          a.download = fileName;
          // Firefox 要求 a 在 DOM 中才会触发下载
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch(() => {
          this.$message.error("导出失败，请确认已登录且文件已生成");
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    // 分社账号 id -> 名称映射（用于回显分社名称）
    loadBranchMap() {
      userLxList({ roleCode: "school-role", pageNo: 1, pageSize: 999 })
        .then((res) => {
          const list =
            (res.result && (res.result.records || res.result)) || [];
          const map = {};
          (Array.isArray(list) ? list : []).forEach((u) => {
            if (u && u.id) map[u.id] = u.realname || u.username || "";
          });
          this.branchMap = map;
        })
        .catch(() => {});
    },
    // 分社名称取值：接口冗余字段优先，缺则用映射
    branchNameOf(record) {
      return (
        record.branchName ||
        record.branchId_dictText ||
        this.branchMap[record.branchId] ||
        record.branchId ||
        "—"
      );
    },
    fileName(record) {
      return (record.filePath || record.previewPath || "")
        .split("/")
        .pop();
    },
    // 打开某条记录的预览弹窗
    openPreview(record) {
      // 只挑 pdf 地址预览：previewPath 优先，filePath 仅当以 .pdf 结尾
      const preview =
        record.previewPath ||
        record.pdfPath ||
        (/\.pdf$/i.test(record.filePath || "") ? record.filePath : "");
      if (!preview) {
        this.$message.error("该报名表暂无预览文件");
        return;
      }
      this.previewVisible = true;
      this.previewRecord = record;
      this.revokePreview();
      this.pdfPath = String(preview).trim();
      this.previewFailed = false;
      this.$nextTick(() => this.fetchPdfBlob(5, 2000));
    },
    closePreview() {
      this.previewVisible = false;
      this.previewRecord = null;
      this.revokePreview();
      this.pdfPath = "";
    },
    // 打开盖章图片预览（与报名表上传页图片预览同源弹窗）
    openPhotoPreview(record) {
      if (!record || !record.photoPath) {
        this.$message.info("该报名表暂无盖章图片");
        return;
      }
      this.photoModalRecord = record;
      this.photoZoom = 1;
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
      this.photoDragging = false;
      this.photoModalVisible = true;
    },
    // 图片缩放：放大 / 缩小 / 重置
    photoZoomIn() {
      this.photoZoom = Math.min(+(this.photoZoom + 0.2).toFixed(2), 4);
    },
    photoZoomOut() {
      this.photoZoom = Math.max(+(this.photoZoom - 0.2).toFixed(2), 0.4);
    },
    photoZoomReset() {
      this.photoZoom = 1;
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
    },
    // 滚轮缩放（向上放大、向下缩小）
    photoWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.photoZoom = Math.min(
        4,
        Math.max(0.4, +(this.photoZoom + delta).toFixed(2))
      );
    },
    // 拖拽平移：按下记录起点
    photoDragStart(e) {
      this.photoDragging = true;
      this.photoDragX = e.clientX;
      this.photoDragY = e.clientY;
    },
    // 拖拽平移：随鼠标移动偏移图片
    photoDragMove(e) {
      if (!this.photoDragging) return;
      const dx = e.clientX - this.photoDragX;
      const dy = e.clientY - this.photoDragY;
      this.photoOffsetX += dx;
      this.photoOffsetY += dy;
      this.photoDragX = e.clientX;
      this.photoDragY = e.clientY;
    },
    // 拖拽平移：松开结束
    photoDragEnd() {
      this.photoDragging = false;
    },
    // 双击图片：平移归位（缩放不变）
    photoDragReset() {
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
    },
    // fetch PDF -> blob -> objectURL 预览（规避 iframe 内嵌 PDF 的限制）
    async fetchPdfBlob(retryLeft = 3, delayMs = 2000) {
      const url = this.pdfUrl;
      if (!url) return;
      try {
        const resp = await fetch(url, {
          credentials: "include",
          cache: "no-cache",
        });
        if (!resp.ok) throw new Error("HTTP " + resp.status);
        const blob = await resp.blob();
        if (!(await this.isPdfBlob(blob))) throw new Error("NOT_PDF");
        if (!this.pdfPath) return; // 期间已关闭/切换
        this.revokePreview();
        this.previewSrc = URL.createObjectURL(blob);
        this.previewFailed = false;
      } catch (e) {
        // 后端可能仍在转换：延迟重试，耗尽再提示
        if (!this.pdfPath) return;
        if (retryLeft > 0) {
          this._pdfRetryTimer = setTimeout(() => {
            this._pdfRetryTimer = null;
            if (this.pdfPath) this.fetchPdfBlob(retryLeft - 1, delayMs);
          }, delayMs);
        } else {
          this.previewFailed = true;
        }
      }
    },
    isPdfBlob(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arr = new Uint8Array(reader.result);
          resolve(
            arr.length >= 5 &&
              arr[0] === 37 &&
              arr[1] === 80 &&
              arr[2] === 68 &&
              arr[3] === 70 &&
              arr[4] === 45
          );
        };
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(blob.slice(0, 5));
      });
    },
    revokePreview() {
      if (this._pdfRetryTimer) {
        clearTimeout(this._pdfRetryTimer);
        this._pdfRetryTimer = null;
      }
      if (this.previewSrc) {
        URL.revokeObjectURL(this.previewSrc);
        this.previewSrc = "";
      }
    },
  },
};
</script>

<style scoped lang="scss">
.rm-page {
  padding: 19px 30px;
  min-height: 600px;
}
/* 顶部统计行（对齐联盟列表页 text1 / color-red） */
.rm-summary {
  display: flex;
  align-items: center;
  margin: 18px 0 11px;
}
/* 导出按钮（样式与其它页面导出按钮一致：primary + download 图标） */
.rm-export-btn {
  margin-left: auto;
}
.text1 {
  font-size: 16px;
  font-family: Microsoft YaHei;
  font-weight: 400;
  color: #000000;
}
.color-red {
  color: #9a0b00;
  margin: 0 4px;
}
/* 表格（对齐联盟列表页：bordered、单元格去内边距固定 60px 行高、浅米色表头） */
.rm-table {
  /deep/ .ant-table-thead > tr > th {
    font-weight: 500;
    text-align: left;
    background: #fafafa;
    border-bottom: 1px solid #e8e8e8;
    text-align: center;
    padding: 0 !important;
    height: 50px;
  }
  /deep/ .ant-table-tbody > tr > td {
    padding: 0 !important;
    height: 60px;
    vertical-align: middle;
  }
  /deep/ .ant-table-tbody > tr:hover > td {
    background: #fff8f0;
  }
}
.rm-branch {
  color: rgba(0, 0, 0, 0.65);
  // font-weight: 600;
  padding: 0 8px;
}
.rm-file-name {
  color: #b25d24;
  font-weight: 600;
  padding: 0 12px;
}
.rm-preview-link {
  color: #9a0b00;
  font-weight: 600;
  &:hover {
    color: #c0392b;
  }
}
/* ===== 报名表预览弹窗：完全复刻首页高职组作品弹窗（indexnew.vue + preview.vue） ===== */
.rm-preview-pop {
  width: 100vw;
  height: 100vh;
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
}
.rm-preview-box {
  margin: 50px auto;
  width: 1177px;
  height: 90%;
}
.rm-c-box {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: url("~@/assets/images/img19.png") no-repeat;
  background-size: 100% 100%;
  overflow: hidden;
}
.rm-header-box {
  height: 80px;
  line-height: 78px;
  position: relative;
  text-align: center;
  background: #9a0b00;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  margin: 0px 19px 0px 9px;
  font-size: 28px;
  font-family: Microsoft YaHei;
  font-weight: bold;
  color: #fff;
  flex: 0 0 80px;
}
.rm-header-title {
  width: 90%;
  margin: 0 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  span {
    font-size: 20px;
    font-weight: normal;
    color: rgba(255, 255, 255, 0.9);
  }
}
.rm-close-box {
  position: absolute;
  color: #fff;
  font-size: 40px;
  right: 3%;
  cursor: pointer;
  top: -2px;
  &:hover {
    color: #ffd27a;
  }
}
.rm-centent-box {
  margin: 0 12px 5px 0;
  padding: 10px 25px;
  height: 85%;
  box-sizing: border-box;
}
.rm-left-preview-box {
  position: relative;
  height: 100%;
}
.rm-img-preview-box {
  position: relative;
  overflow: hidden;
  height: 100%;
  background: #fff;
}
.rm-state-box {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 16px;
  color: #555;
  background: #fff;
}
.rm-state-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  a {
    color: #9a0b00;
    text-decoration: underline;
  }
}
.rm-state-link {
  color: #9a0b00;
  text-decoration: underline;
  font-size: 14px;
  margin-top: 10px;
}
.rm-no-photo {
  color: #bbb;
}
/* 盖章图片预览弹窗：复用报名表预览弹窗外壳（rm-preview-pop 等），此处仅补充工具栏与图片区样式 */
.rm-photo-wrap {
  display: flex;
  flex-direction: column;
}
.rm-photo-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.rm-photo-scale {
  min-width: 48px;
  text-align: center;
  font-size: 14px;
  color: #9a0b00;
  font-weight: bold;
}
.rm-photo-tip {
  margin-left: auto;
  font-size: 12px;
  color: #999;
}
.rm-photo-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}
.rm-photo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  transform-origin: center center;
  cursor: grab;
}
</style>
